import { API } from 'utils/Helper';
import React, { Component, Suspense } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Helmet } from "react-helmet";
import i18next from "i18next";
import { I18nextProvider, useTranslation } from "react-i18next";
import { Router } from "react-router-dom";
import history from "utils/history";

import { useThemeConfig } from 'context/ThemeConfigContext';
import BcRouter from "themes/bc/Router";
import BcLayout from "themes/bc/Layout";
import StakeRouter from "themes/stake/Router";
import StakeLayout from "themes/stake/Layout";

// Theme-specific modals
import BcSingleGameModal from "themes/bc/components/modals/SingleGameModal";
import StakeSingleGameModal from "themes/stake/components/modals/SingleGameModal";
import BcUserModal from "themes/bc/components/modals/UserModal";
import StakeUserModal from "themes/stake/components/modals/UserModal";
import BcGameModal from "themes/bc/components/modals/GameModal";
import StakeGameModal from "themes/stake/components/modals/GameModal";
import BcCustomLoader from "themes/bc/components/common/CustomLoader";
import StakeCustomLoader from "themes/stake/components/common/CustomLoader";
import CampaignPopupModal from "components/CampaignPopupModal";

import { __, decode, Event, DEVELOPMENT } from './utils/Helper';
import styled from "styled-components";

i18next.init({
  interpolation: { escapeValue: false },
});

const delay = DEVELOPMENT ? 300 : 2000;


function App({ props }) {

  const siteName = window?.siteConfig?.website_name || process.env.REACT_APP_SITE_NAME || 'WinSmash';

  return (
    <>
      <Helmet>
        <title>{siteName}</title>
        <meta
          name="description"
          content={`${siteName}. With our unique community and a huge selection of games like Crash, HashDice, Plinko, Slots, and many more.`}
        />
        <meta
          name="keywords"
          content={`Online , Slot Games, Crash Game, Dice Games, Live , , Blockchain Games, , Best Online Game, Gambling Games, ${siteName} Games`}
        />
        <meta property="og:title" content={`${siteName} - The 20 Best Gambling Games`} />
        <meta
          property="og:description"
          content={`${siteName}. With our unique community and a huge selection of games like Crash, HashDice, Plinko, Slots, and many more.`}
        />
        <meta property="og:image" content={window?.siteConfig?.logo_big ? (API + window.siteConfig.logo_big) : "/assets/images/logo.png"} />
      </Helmet>
      <Suspense fallback={<></>}>
        <Page />
      </Suspense>
    </>

  );
}

function Page() {
  const { t } = useTranslation();
  const { activeDesign } = useThemeConfig();
  return <Application t={t} activeDesign={activeDesign} />;
}



const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
  background-color: #24262b;
  border-radius: 8px;
  height: 100vh;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #fff;
  margin-bottom: 1.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin-bottom: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1rem;
`;

const FieldContainer = styled.input`
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: #dc3545;
  margin-top: 1rem;
`;

class Application extends React.Component {
  _isMounted = false;

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      gameLoaded: false,
      key: "",
      domain: "",
      ipAddress: "",
      errorMessage: "",
      verifying: false,
    };
  }

  componentDidMount() {
    this._isMounted = true;

    // Fetch dynamic site configuration
    fetch(`${API}/website-config`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.data) {
          const config = data.data;
          window.siteConfig = config;
          if (config.website_name) {
            document.title = config.website_name;
            const titleEl = document.querySelector('title');
            if(titleEl) titleEl.innerText = config.website_name;
          }
          if (config.favicon) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = API + config.favicon;
          }
          // Set Dynamic Manifest
          try {
            const manifest = {
              short_name: config.website_name || "Betzplay",
              name: config.website_name || "Betzplay",
              icons: [
                {
                  src: config.favicon ? API + config.favicon : "favicon.png",
                  sizes: "64x64 32x32 24x24 16x16",
                  type: "image/png"
                }
              ],
              start_url: ".",
              display: "standalone",
              theme_color: "#000000",
              background_color: "#ffffff"
            };
            const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
            const manifestURL = URL.createObjectURL(blob);
            let manifestLink = document.querySelector("link[rel='manifest']");
            if (manifestLink) {
              manifestLink.setAttribute('href', manifestURL);
            }
          } catch (e) {
            console.error("Error setting dynamic manifest", e);
          }
          // Optionally trigger a re-render if needed
          this.forceUpdate();
        }
      })
      .catch(err => console.error("Error fetching website config:", err));
    
    // Check for social login tokens in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const refreshToken = urlParams.get('refreshToken');
    const authError = urlParams.get('error');

    if (token) {
      import('utils/Storage').then(({ default: storage }) => {
        storage.setKey('token', token);
        if (refreshToken) {
           storage.setKey('refreshToken', refreshToken);
        }
        storage.setKey('logged', true);
        window.history.replaceState({}, document.title, window.location.pathname);
        setTimeout(() => {
          import('utils/Helper').then(({ Event }) => {
            Event.emit('login');
            window.location.reload();
          });
        }, 500);
      });
    } else if (authError) {
      import('utils/Helper').then(({ sendNotfication }) => {
        let msg = 'Social Login Failed. Please try again.';
        if (authError === 'google_login_disabled') msg = 'Google Login is currently disabled by Administrator.';
        if (authError === 'facebook_login_disabled') msg = 'Facebook Login is currently disabled by Administrator.';
        if (authError === 'social_auth_failed') msg = 'There was a problem authenticating with the social provider.';
        sendNotfication(msg, 'error');
      });
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    this.setState({ gameLoaded: true, loading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }


  render() {
    const { t, activeDesign } = this.props;

    // Theme-specific modals & loaders selection
    const UserModalComponent = activeDesign === 'stake' ? StakeUserModal : BcUserModal;
    const GameModalComponent = activeDesign === 'stake' ? StakeGameModal : BcGameModal;
    const SingleGameModalComponent = activeDesign === 'stake' ? StakeSingleGameModal : BcSingleGameModal;
    const CustomLoaderComponent = activeDesign === 'stake' ? StakeCustomLoader : BcCustomLoader;

    // If loading, show the loading animation
    if (this.state.loading) {
      return <CustomLoaderComponent show={true} />;
    }

    // Dynamic routing/layout selection
    const LayoutComponent = activeDesign === 'stake' ? StakeLayout : BcLayout;
    const RouterComponent = activeDesign === 'stake' ? StakeRouter : BcRouter;

    return (
      <BrowserRouter>
        <I18nextProvider i18n={i18next}>
          <Router history={history}>
            {this._isMounted && (
              <>
                {/* <ReactNotifications /> */}
                <ToastContainer
                  position={window.innerWidth <= 768 ? "bottom-center" : "top-right"}
                  autoClose={4000}
                  hideProgressBar={true}
                  newestOnTop={true}
                  closeOnClick
                  pauseOnHover
                  draggable={false}
                  theme="dark"
                  style={{
                    bottom: window.innerWidth <= 768 ? "80px" : "24px",
                    zIndex: 999999,
                  }}
                  toastStyle={{
                    background: "linear-gradient(135deg, #1a2c38 0%, #0f1f2a 100%)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "12px",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.6)",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: "500",
                    padding: "12px 16px",
                  }}
                />
                <UserModalComponent t={t} />
                <GameModalComponent t={t} />
                <SingleGameModalComponent t={t} props={this.props} />
                <CampaignPopupModal />
              </>
            )}
            <LayoutComponent>
              <RouterComponent t={t} history={history} />
            </LayoutComponent>
          </Router>
        </I18nextProvider>
      </BrowserRouter>
    );
  }
}

App.propTypes = {
  coin: PropTypes.string,
  chart_coin: PropTypes.string,
};

const mapStateToProps = (state) => ({
  coin: state.items.coin,
  chart_coin: state.items.chart_coin,
});

export default connect(mapStateToProps, {})(App);
